import type { TyreLayoutSlot, TyreVehicleKind, VehicleTyreLayout } from '@chukua/common';
import { useState } from 'react';
import type { TyreMoveFormValues, TyreMoveKind } from '../policies/tyre-move.policy';
import { useTyreMove } from './use-tyre-move';

interface UseVehicleTyreActionsArgs {
  vehicleKind: TyreVehicleKind;
  vehicleId: string;
  layout: VehicleTyreLayout | null;
  reload: () => Promise<unknown>;
}

/**
 * The move a person has started from one position of a vehicle's layout (fit, replace or rotate) and how it is sent. The vehicle and position
 * come from the row; rotating into an occupied position names the tyre there, which the server swaps.
 */
export function useVehicleTyreActions({ vehicleKind, vehicleId, layout, reload }: UseVehicleTyreActionsArgs) {
  const slots = layout?.slots ?? [];
  const move = useTyreMove(reload);
  const [action, setAction] = useState<{ kind: TyreMoveKind; slot: TyreLayoutSlot } | null>(null);

  /** The tyre the move is about; for a fit there is none yet, so the position stands in for the title. */
  const subject = action ? (action.slot.tyre ?? { id: '', serial: action.slot.positionCode }) : null;

  const rotateOptions = slots
    .filter((slot) => !slot.isRetired && slot.positionCode !== action?.slot.positionCode)
    .map((slot) => ({ value: slot.positionCode, label: slot.tyre ? `${slot.positionCode} — swap with ${slot.tyre.serial}` : `${slot.positionCode} — empty` }));

  async function submit(values: TyreMoveFormValues): Promise<boolean> {
    if (!action || !subject) return false;
    const occupant = values.toPositionCode ? slots.find((slot) => slot.positionCode === values.toPositionCode)?.tyre : null;
    return move.run(action.kind, subject, values, {
      vehicle: { kind: vehicleKind, id: vehicleId },
      positionCode: action.kind === 'fit' ? action.slot.positionCode : undefined,
      swapWithTyreId: occupant?.id,
    });
  }

  return {
    move,
    action,
    subject,
    rotateOptions,
    /** What the rotate modal needs to let the destination be picked on the vehicle. */
    rotation: layout && action?.kind === 'rotate' ? { layout, fromPositionCode: action.slot.positionCode } : null,
    submit,
    open: (kind: TyreMoveKind, slot: TyreLayoutSlot) => {
      move.resetError();
      setAction({ kind, slot });
    },
    close: () => setAction(null),
  };
}
